import React, { ForwardedRef, forwardRef } from 'react'
import { circleAvatarOptions } from 'services/filestack'
import { Avatar, Typography } from '@mui/material'
import { lighten } from '@mui/material/styles'
import { FormValueMeta, FormValueInput, Nullable } from 'types'
import Condition from 'components/Condition'
import Picture from 'components/Picture'
import Button from 'components/Button'
import { useUploadFile } from 'hooks/filestack'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  avatar: {
    width: '100px !important',
    height: '100px !important',
    marginBottom: theme.spacing(2),
  },
  description: {
    flex: '1 0 content',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  title: {
    flex: '1 0 auto',
    color: lighten(theme.palette.text.dark, 0.25),
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  button: {
    marginTop: theme.spacing(2),
    flex: '1 0 auto',
  },
  error: {
    color: (theme.palette.error as any)[500],
  },
}))

interface IFileUploadProps {
  meta?: FormValueMeta
  input: FormValueInput<Nullable<string>>
  title?: string
  label?: string
  avatar?: boolean
  color?: 'secondary' | 'primary'
}

const FileUpload = (props: IFileUploadProps, ref: ForwardedRef<HTMLButtonElement>) => {
  const { classes } = useStyles()
  const { input: { value, onChange }, meta: { touched, error } = {}, title, label, avatar } = props

  const filestack = useUploadFile({
    onChange: file => onChange(file.url),
    options: avatar ? circleAvatarOptions() : {},
  })

  return (
    <div className={classes.container}>
      {value && (
        <Avatar className={classes.avatar}>
          <Picture src={value} style={{ width: '100%' }} />
        </Avatar>
      )}

      <div className={classes.description}>
        <Condition condition={Boolean(title)}>
          <Typography variant="subtitle1" className={classes.title}>
            {title}
          </Typography>
        </Condition>

        <Button
          disabled={filestack.isOpen}
          color="primary"
          className={classes.button}
          onClick={filestack.openPicker}
          ref={ref}
        >
          {label || 'SELECT FILE'}
        </Button>

        <Condition condition={Boolean(touched && error)}>
          <Typography variant="body1" className={classes.error}>
            {error}
          </Typography>
        </Condition>
      </div>
    </div>
  )
}

export default forwardRef(FileUpload)
